import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import Layout from '../Layout'
import DataTable from '../../../Components/DataTable'
import Button from '../../../Components/Button'
import FilterPills from '../../../Components/FilterPills'
import ResourceIndexHeader from '../../../Components/ResourceIndexHeader'
import ExportMenuItemSalesModal from './ExportMenuItemSalesModal'
import { useDeleteWithConfirmation } from '../../../hooks/useDeleteWithConfirmation'
import { formatPrice } from '../../../utils/formatters'

export default function Index({
  account,
  accounts,
  navigation,
  menu_items,
  categories,
  sittings,
  params = {},
  is_admin
}) {
  const [showExportModal, setShowExportModal] = useState(false)
  const handleDelete = useDeleteWithConfirmation('/manage/menu_items')

  const handleCategoryChange = (category) => {
    router.visit('/manage/menu_items', {
      data: { ...params, category: category || undefined },
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleSittingChange = (sitting) => {
    router.visit('/manage/menu_items', {
      data: { ...params, sitting: sitting || undefined },
      preserveState: true,
      preserveScroll: true
    })
  }

  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (value, item) => (
        <div className="flex items-center">
          {item.image_url && (
            <img
              src={item.image_url}
              alt={item.name}
              className="h-10 w-10 rounded-lg object-cover mr-3"
            />
          )}
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            {item.description && (
              <div className="text-gray-500 truncate max-w-xs">{item.description}</div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      className: 'text-gray-500 capitalize'
    },
    {
      key: 'sittings',
      label: 'Sittings',
      className: 'text-gray-500 capitalize',
      render: (value) => (value || []).join(', ')
    },
    {
      key: 'price_pence',
      label: 'Price',
      sortable: true,
      className: 'font-medium text-gray-900',
      render: (value) => formatPrice(value)
    },
    {
      key: 'visible',
      label: 'Status',
      render: (value) => (
        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
          value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Visible' : 'Hidden'}
        </span>
      )
    }
  ]

  return (
    <Layout
      account={account}
      accounts={accounts}
      navigation={navigation}
      title="Menu Items"
      is_admin={is_admin}
    >
      <ResourceIndexHeader
        count={menu_items.length}
        singular="menu item"
        plural="menu items"
        addHref="/manage/menu_items/new"
        addLabel="Add Menu Item"
        actions={
          <Button variant="secondary" onClick={() => setShowExportModal(true)}>
            Export
          </Button>
        }
      />

      {/* Category Tabs */}
      <div className="flex gap-1 mb-4">
        <button
          onClick={() => handleCategoryChange('')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition ${
            !params.category
              ? 'bg-sky-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => handleCategoryChange(cat)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition capitalize ${
              params.category === cat
                ? 'bg-sky-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Sitting Filter Pills */}
      <div className="mb-6">
        <FilterPills
          items={sittings}
          value={params.sitting}
          onChange={handleSittingChange}
          allLabel="All Sittings"
        />
      </div>

      <DataTable
        data={menu_items}
        columns={columns}
        actions={(item) => (
          <>
            <Button link href={`/manage/menu_items/${item.id}/edit`}>Edit</Button>
            <Button
              link
              variant={item.visible ? "muted" : "primary"}
              onClick={() => router.patch(`/manage/menu_items/${item.id}/toggle_visibility`)}
            >
              {item.visible ? 'Hide' : 'Show'}
            </Button>
            <Button link variant="danger" onClick={() => handleDelete(item)}>Delete</Button>
          </>
        )}
        searchable={true}
        searchPlaceholder="Search menu items..."
        emptyState={{
          message: 'No menu items found',
          action: { label: 'Add your first menu item', href: '/manage/menu_items/new' }
        }}
        baseUrl="/manage/menu_items"
        params={params}
      />

      {showExportModal && (
        <ExportMenuItemSalesModal onClose={() => setShowExportModal(false)} />
      )}
    </Layout>
  )
}
